import React from 'react';
import { useMemo } from 'react';

import isMobile from 'utils/isMobile';
import { useTranslator } from 'utils/translator';
import { priorityOrderBy } from 'utils/helpers';

const useTableColumns = (columns, additionalColumns = [], columnsList) => {
  const { t } = useTranslator();

  const visibleColumns = useMemo(() => {
    const baseList = columnsList.length ? columnsList : columns;
    return Object.values(baseList).filter(({ hide }) => !hide);
  }, [columns, columnsList]);

  const tableColumns = useMemo(() => [...additionalColumns, ...visibleColumns], [additionalColumns, visibleColumns]);

  const exportedList = useMemo(() => {
    const filtered = columns.filter(col =>
      columnsList.length === 0
        ? !col.hide || col.forceExport
        : columnsList.some(item => item.dataKey === col.dataKey) || col.forceExport
    );
    return [...additionalColumns, ...filtered];
  }, [columns, columnsList, additionalColumns]);

  return useMemo(() => {
    const totalInitialValues = {};
    const group = {};

    const tableColumnsList = tableColumns.map(column => {
      const { groupType, showTotal, dataKey, text, tooltipText } = column;

      if (showTotal) totalInitialValues[dataKey] = 0;
      if (groupType !== undefined) {
        const key = groupType === '' ? 'withoutGroupType' : groupType;
        group[key] = group[key]
          ? { ...column, ...group[key], innerColCount: group[key].innerColCount + 1 }
          : { innerColCount: 1, text: groupType ? t(groupType) : '' };
      }

      const translatedText = typeof text === 'function' ? text() : t(text);

      return {
        ...column,
        text: translatedText,
        render: column.render ?? (tooltipText ? () => <span title={tooltipText}>{translatedText}</span> : undefined),
      };
    });

    const orderedTableColumns = isMobile() ? priorityOrderBy(tableColumnsList) : tableColumnsList;
    const orderedExportedList = isMobile() ? priorityOrderBy(exportedList) : exportedList;

    return {
      tableColumns: orderedTableColumns,
      exportedList: orderedExportedList,
      groupList: Object.values(group),
      totalInitialValues: Object.keys(totalInitialValues).length
        ? { [tableColumns[0].dataKey]: t('total'), ...totalInitialValues }
        : {},
    };
  }, [t, tableColumns, exportedList]);
};

export default useTableColumns;
